import { Field, ScopeOption } from "../../../../../models/CommonModel";

/** The two pseudo-fields rendered as standalone Remote Data / Remote Fields toggles, not regular fields. */
export const REMOTE_DATA_FIELD = "remote_data";
export const REMOTE_FIELDS_FIELD = "remote_fields";

/** Field names excluded from the table's regular field list (surfaced as Remote toggles instead). */
export const NON_FIELD_TOGGLES = [REMOTE_DATA_FIELD, REMOTE_FIELDS_FIELD];

/**
 * The single global model toggled by every object's "remote_fields" toggle. Enabling remote
 * fields anywhere enables this model; its enabled state is what the toggle reflects.
 */
export const REMOTE_FIELD_CLASS_MODEL = "RemoteFieldClass";

/** Description shown in the remote_data row's description column. */
export const REMOTE_DATA_DESCRIPTION =
  "API responses from the third-party in their original format.";

/** Description shown in the remote_fields row's description column. */
export const REMOTE_FIELDS_DESCRIPTION =
  "Standard third-party platform fields and custom Linked Account fields that aren't mapped to the Common Model.";

/** Explanatory tooltip copy for the Remote toggles. Docs URLs live in router/RouterUtils. */
export const REMOTE_DATA_INFO_TEXT =
  "Remote Data lets you access data from third-party integrations in its original format, even if it's not in our Common Model. Merge does not normalize Remote Data, so the structure may vary between integrations.";
export const REMOTE_FIELDS_INFO_TEXT =
  "Remote Fields let you access additional fields that aren't mapped to Merge's Common Model, including standard third-party fields and custom fields on a Linked Account.";

/** Tooltip copy explaining why a scope switch is disabled. */
export const SCOPE_DISABLED_ORG_MESSAGE =
  "Editing Scopes has been disabled at the organizational level";
export const SCOPE_DISABLED_FIELD_UPGRADE_MESSAGE = "Upgrade your plan to manage field Scopes";
export const SCOPE_DISABLED_REMOTE_UPGRADE_MESSAGE =
  "Upgrade your plan to manage Remote Data Scopes";
export const scopeDisabledSubObjectMessage = (subObjectName: string) =>
  `Enable the ${subObjectName} object to toggle its fields`;

/** Explains the "Optional" scope, shown on an info icon next to the Optional toggle. */
export const SCOPE_OPTIONAL_EXPLANATION =
  "Your users can choose whether to enable this in Merge Link";

/**
 * Display label for a field's type. A uuid-format relation can render two ways:
 * when it expands into sub-fields (related to an endpoint-less sub-object) the API returns a
 * nested object, so it reads "object"; otherwise it's a foreign-key reference that returns the
 * related row's UUID, so it reads "uuid". Non-relation fields use their raw type.
 */
export const fieldTypeLabel = (field: Field, isSubObject = false): string => {
  if (isSubObject) return "object";
  if (field.field_format === "uuid") return "uuid";
  return field.field_type.toLowerCase();
};

export const SCOPE_LABEL: Record<ScopeOption, string> = {
  [ScopeOption.ENABLED]: "Enabled",
  [ScopeOption.DISABLED]: "Disabled",
  [ScopeOption.OPTIONAL]: "Optional",
};

/**
 * Shared column widths so the header row and each object/field row line up.
 * read/write are sized to fit the 3-option edit Switch (~260px). object is flexible
 * but caps at a 472px max (per design) and wraps a long name onto multiple lines beyond
 * that; its pr-6 gutter keeps a long name from butting against the Read column when the
 * table is near its min-width. The table sets a min-width floor and scrolls horizontally
 * when narrower (see ScopesTable) so columns never overlap and the Card's right padding
 * is preserved. (472px has no Tailwind maxWidth token, so it's an arbitrary value.)
 */
export const SCOPE_COLUMN_CLASS = {
  object: "min-w-0 flex-1 pr-6 max-w-[472px]",
  read: "w-72 shrink-0",
  write: "w-72 shrink-0",
  // Own column for a model's "Deprecated" badge, so it never crowds the field count.
  status: "w-28 shrink-0",
  // Field-count column ("9/10 enabled") with the "Fields" header above it.
  fields: "w-32 shrink-0",
  // Rightmost column: the row's expand chevron, with the header's docs link aligned above it.
  expand: "w-40 shrink-0",
};

/** Min width below which the scopes table scrolls horizontally instead of collapsing columns. */
export const SCOPE_TABLE_MIN_WIDTH_CLASS = "min-w-[1128px]";

/** Default sort for the scopes toolbar; matches the table's historical A-Z object order. */
export const DEFAULT_SCOPES_ORDERING = "name";

/** Sort options for the toolbar's "Sort by" dropdown. `value` is the backend `ordering` param. */
export const SCOPES_SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "name", label: "Name (A-Z)" },
  { value: "-name", label: "Name (Z-A)" },
  { value: "-fields_enabled", label: "Fields enabled (High-Low)" },
  { value: "fields_enabled", label: "Fields enabled (Low-High)" },
];

/** "Any" = no filter on that direction; not sent to the server. */
export const SCOPES_ACTION_STATE_ANY = "any";

/** The "optional" state is only meaningful when the org has optional scopes enabled; the toolbar
 *  drops this option otherwise (picking it would always return no results). */
export const SCOPES_ACTION_STATE_OPTIONAL = "optional";

/** Read/Write state options for the toolbar's Filter menu. Non-"any" values are the backend
 *  `read` / `write` query params. */
export const SCOPES_ACTION_STATE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: SCOPES_ACTION_STATE_ANY, label: "Any" },
  { value: "enabled", label: "Enabled" },
  { value: SCOPES_ACTION_STATE_OPTIONAL, label: "Optional" },
  { value: "disabled", label: "Disabled" },
];
